// CorplistFormModal.jsx
import React, { useState } from "react";
import ReactModal from "react-modal";
import { FormProvider } from "react-hook-form";
import { Input, Select, Draggable } from "@components";
import { useSiteFormModal } from "@hooks";
import "./CorpregCustom.css"; // 스타일 (input-customtext, error-custommessage, etc.)

const SitelistFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  isEdit,
  boardNum,
  maxFileCount = 1,
  isAdmin
}) => {
  // useCompanyFormModal에서 등록/수정 로직(백엔드 API 호출 및 유효성 검사)
  const {
    handleFormSubmit,
    setValue,
    watch,
    reset,
    loadingDetail,
    detailError,
    formState: { errors }, // <-- react-hook-form에서 errors 가져오기
    ...methods
  } = useSiteFormModal({
    isOpen,
    isEdit,
    boardNum,
    onClose,
    onSuccess,
    maxFileCount,
    isAdmin
  });

  // [1] 모달 크기 통일 (등록/수정 구분 없이 항상 650px 예시)
  const position = Draggable({ isOpen, dragHandle: ".modal-header" });
  const containerStyle = {
    maxWidth: "1150px",
    minWidth: "1150px",
    maxHeight: "650px",  // 통일
    minHeight: "650px",  // 통일
    transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px)`,
  };

  // [2] 숫자만 입력 허용(사업자등록번호, 전화번호) 로직
  const [isComposing, setIsComposing] = useState(false);

  const handleNumericKeyDown = (e) => {
    if (isComposing) return;
    const allowKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Enter",
      "Home",
      "End",
    ];
    if (allowKeys.includes(e.key)) return;
    if (e.shiftKey || e.altKey || e.ctrlKey) {
      e.preventDefault();
      return;
    }
    // 숫자 이외 키 막기
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleNumericPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain") || "";
    const onlyDigits = text.replace(/\D/g, "");
    setValue(e.target.name, onlyDigits, { shouldValidate: true });
  };

  const handleNumericDrop = (e) => {
    e.preventDefault();
    const text = e.dataTransfer.getData("text/plain") || "";
    const onlyDigits = text.replace(/\D/g, "");
    setValue(e.target.name, onlyDigits, { shouldValidate: true });
  };

  const handleCompositionStart = () => setIsComposing(true);
  const handleCompositionEnd = (e) => {
    setIsComposing(false);
    const onlyDigits = e.target.value.replace(/\D/g, "");
    e.target.value = onlyDigits;
    setValue(e.target.name, onlyDigits, { shouldValidate: true });
  };

  // [3] 주소 검색
  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: (data) => {
        setValue("addr1", data.address, { shouldValidate: true });
      },
    }).open();
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName="modal-overlay"
      className="modal-wrap"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleFormSubmit}>
          <div className="modal-container" style={containerStyle}>
            {/* 모달 헤더 영역 */}
            <div className="modal-header" style={{ cursor: "move" }}>
              <h5>
                사업장 정보{" "}
                <span className="modal-header-span">
                  {isEdit ? "수정" : "등록"}
                </span>
              </h5>
              <button
                type="button"
                className="modal-btn-close"
                onClick={onClose}
              >
                <span className="modal-btn-span">X</span>
              </button>
            </div>

            {/* 모달 내용 영역 */}
            <div className="modal-content">
              {loadingDetail && isEdit ? (
                <div>데이터 로딩 중...</div>
              ) : detailError ? (
                <div>오류 발생: {detailError.message}</div>
              ) : (
                <table className="table-write" style={{ width: "100%" }}>
                  <tbody>
                    <tr>
                      <th>사업장 코드</th>
                      <td>
                        <Input
                          name="siteCode"
                          placeholder="자동생성"
                          type="number"
                          readOnly
                          className="input-customtext"
                          style={{ width: "200px" }}
                        />
                      </td>
                      <th>
                        회사 코드
                      </th>
                      <td>
                        <Input
                          name="compCode"
                          readOnly
                          className="input-customtext"
                          style={{ width: "200px" }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        사업장명<span className="requiredcustom">*</span>
                      </th>
                      <td>
                        <Input
                          name="siteName"
                          placeholder="사업장명"
                          maxLength={20}
                          className={`input-customtext ${
                            errors.siteName ? "error" : ""
                          }`}
                        />
                        {errors.siteName && (
                          <p className="error-custommessage">
                            {errors.siteName.message}
                          </p>
                        )}
                      </td>
                      <th>
                        사업자등록번호<span className="requiredcustom">*</span>
                      </th>
                      <td>
                        <Input
                          name="bizNo"
                          placeholder="사업자등록번호(- 제외)"
                          maxLength={10}
                          className={`input-customtext ${errors.bizNo ? "error" : ""}`}
                          onKeyDown={handleNumericKeyDown}
                          onPaste={handleNumericPaste}
                          onDrop={handleNumericDrop}
                          onCompositionStart={handleCompositionStart}
                          onCompositionEnd={handleCompositionEnd}
                        />
                        {errors.bizNo && (
                          <p className="error-custommessage">{errors.bizNo.message}</p>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>
                        대표자명<span className="requiredcustom">*</span>
                      </th>
                      <td>
                        <Input
                          name="repName"
                          placeholder="대표자명"
                          maxLength={20}
                          className={`input-customtext ${
                            errors.repName ? "error" : ""
                          }`}
                        />
                        {errors.repName && (
                          <p className="error-custommessage">{errors.repName.message}</p>
                        )}
                      </td>
                      <th>전화번호</th>
                      <td>
                        <Input
                          name="phone"
                          placeholder="전화번호(- 제외)"
                          maxLength={11}
                          className={`input-customtext ${
                            errors.phone ? "error" : ""
                          }`}
                          onKeyDown={handleNumericKeyDown}
                          onPaste={handleNumericPaste}
                          onDrop={handleNumericDrop}
                          onCompositionStart={handleCompositionStart}
                          onCompositionEnd={handleCompositionEnd}
                        />
                        <p style={{ fontSize: '11px', color: '#888', margin: '3px 0 0' }}>- 없이 숫자만 입력하세요.</p>
                        {errors.phone && (
                          <p className="error-custommessage">{errors.phone.message}</p>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>
                        주소<span className="requiredcustom">*</span>
                      </th>
                      <td colSpan={3}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Input
                              name="addr1"
                              placeholder="주소를 검색하세요"
                              className={`input-customtext ${
                                errors.addr1 ? "error" : ""
                              }`}
                              style={{ width: "400px" }}
                            />
                            <button
                              type="button"
                              className="btn-save"
                              style={{ marginLeft: "8px" }}
                              onClick={handleAddressSearch}
                            >
                              검색
                            </button>
                          </div>
                          {errors.addr1 && (
                            <p className="error-custommessage">{errors.addr1.message}</p>
                          )}
                          <div>
                            <Input
                              name="addr2"
                              placeholder="상세주소"
                              maxLength={50}
                              className={`input-customtext ${
                                errors.addr2 ? "error" : ""
                              }`}
                              style={{ width: "400px" }}
                            />
                            {errors.addr2 && (
                              <p className="error-custommessage">
                                {errors.addr2.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>비고</th>
                      <td colSpan={3}>
                        <div style={{ textAlign: "right", marginBottom: "4px" }}>
                          {watch("notice")?.length || 0}/2000
                        </div>
                        <textarea
                          name="notice"
                          placeholder="비고"
                          maxLength={2000}
                          className={`input-customtextarea ${
                            errors.notice ? "error" : ""
                          }`}
                          rows={3}
                          style={{ width: "100%" }}
                          {...methods.register("notice")}
                        />
                        {errors.notice && (
                          <p className="error-custommessage">{errors.notice.message}</p>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            {/* 모달 하단 버튼 */}
            <div className="modal-btn-wrap btn-right-box">
              <ul>
                <li>
                  <button type="button" className="btn-cancel" onClick={onClose}>
                    취소
                  </button>
                </li>
                <li>
                  <button type="submit" className="btn-save">
                    저장
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </form>
      </FormProvider>
    </ReactModal>
  );
};

export { SitelistFormModal };
